import React, { FC, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import InputLabel2 from '../../inputLabel/InputLabel2';
import { texts } from './constants';
import * as styles from './styles';
import { InputBackground, InputProps, InputType } from './models';

const ErrorMessage = styled.span`
  ${styles.ErrorMessage}
`;
const WarningMessage = styled.span`
  ${styles.WarningMessage}
`;
const InputInnerWrapper = styled.div`
  ${styles.InputInnerWrapper}
`;
const StyledInput = styled.input`
  ${styles.Input}
`;
const StyledInputWrapper = styled.div`
  ${styles.InputWrapper}
`;

const CurrencySymbol = styled.span`
  ${styles.CurrentSymbol};
`;

//TODO: This component has been transformed to FC and got spliced
// This changes was only for readability and the logic kept as it was.
// There is still a need for logic improvements here.
const Input: FC<InputProps> = (props) => {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.currentTarget;

      if (props.pattern && !new RegExp(props.pattern).test(value)) return;

      props.onChange?.(event);
    },
    [props.pattern, props.onChange]
  );

  const onKeyPress = useCallback(
    (event) => {
      const key = event.keyCode || event.which;
      if (key === 13) {
        // perform your Logic on "enter" button
        typeof props.onKeyEnter === 'function' ? props.onKeyEnter() : props.onSubmit?.();
      }
    },
    [props.onKeyEnter, props.onSubmit]
  );

  const onFocus = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsFocused(event.type === 'focus');
      props.toggleFocus?.(event);
    },
    [props.toggleFocus]
  );

  const onBlur = useCallback(
    (args) => {
      setIsFocused(event.type === 'blur');
      props.onBlur?.(args);
      props.toggleFocus?.(args);
    },
    [props.onBlur, props.toggleFocus]
  );

  const errorBox = useMemo(() => {
    if (props.error || props.initErrorSpacing) {
      const testInfo = `${props.name}Error`;

      // Should be rewritten
      let errorContent;
      if (props.initErrorSpacing) errorContent = props.error;
      else {
        // Bad practice using local text
        errorContent = typeof props.error === 'string' ? props.error : texts.ERROR;
      }

      return (
        <ErrorMessage
          {...{
            className: 'error-message',
            initErrorSpacing: props.initErrorSpacing,
            'data-at': testInfo,
            'data-testid': 'error-message',
            tabIndex: 1,
          }}
        >
          {errorContent}
        </ErrorMessage>
      );
    }
  }, [props.initErrorSpacing, props.error, props.name]);

  const warningBox = useMemo(() => {
    if (!props.error && (props.warning || props.initWarningSpacing)) {
      const testInfo = `${props.name}Warning`;

      return (
        <WarningMessage
          {...{
            className: 'warning-message',
            initWarningSpacing: props.initWarningSpacing,
            'data-at': testInfo,
            'data-testid': 'warning-message',
            tabIndex: 1,
          }}
        >
          {props.warning}
        </WarningMessage>
      );
    }
  }, [props.initWarningSpacing, props.error, props.name, props.warning]);

  const isNumberType = props.type === InputType.Number || props.type === InputType.Money;

  const labelText = props.label && typeof props.label === 'string' && props.label.toString();
  const titleText = props.animatedLabel || (!props.hidePlaceholderAsTitle && props.placeholder) || labelText;

  const buttonRef = useRef<HTMLDivElement>();

  const [customButtonSizes, setCustomButtonSizes] = useState<undefined | { width: number; height: number }>();

  // If a button is provided - sets the size of it after DOM changes.
  useLayoutEffect(() => {
    if (!!buttonRef?.current) {
      const newCustomButtonSizes = { width: buttonRef?.current?.offsetWidth, height: buttonRef?.current?.offsetHeight };
      setCustomButtonSizes(newCustomButtonSizes);
    }
  }, []);

  const showCurrencySymbol = useMemo(() => {
    const { value, type, placeholder } = props;
    return type === InputType.Money && !!placeholder && (isFocused || value);
  }, [props.value, isFocused, props.placeholder, props.type]);

  const inputOptions = {
    inputMode: props.inputMode || (isNumberType ? 'numeric' : undefined),
    pattern: isNumberType ? '[0-9]*' : undefined,
    id: props.id || props.name,
    name: props.name,
    value: props.value,
    type: props.type === InputType.Password ? InputType.Password : InputType.Text,
    checked: !!props.value,
    readOnly: props.disabled,
    autoFocus: props.autoFocus,
    blue: !!props.blue,
    background: props.background,
    focus: !!props.focus,
    small: !!props.small,
    animatedLabel: props.animatedLabel,
    placeholder: props.placeholder,
    autoComplete: props.autoComplete || 'off',
    autoCapitalize: 'none',
    autoCorrect: 'off',
    maxLength: props.maxLength,
    'data-at': props.dataAt,
    'data-testid': props.dataTestId,
    onChange: handleOnChange,
    onClick: props.onClick,
    onKeyUp: props.onKeyUp,
    toggleFocus: onFocus,
    centeredText: props.centeredText,
    onKeyPress,
    onBlur,
    onFocus,
    isSecuredInput: props.isSecuredInput,
    tabIndex: 0,
    ref: props.innerRef,
    required: typeof props.required !== 'undefined' ? props.required : true,
    customButtonSizes,
    isAnimated: props.isAnimated,
    showCurrencySymbol: !!showCurrencySymbol,
  };

  return (
    <fieldset {...{ className: props.className }}>
      <StyledInputWrapper {...{ disabled: props.disabled }}>
        {props.label && !props.isAnimated && (
          <InputLabel2 {...{ inputId: props.name, inputValue: props.value, dataAt: `${props.dataAt}-label` }}>
            {props.label}
          </InputLabel2>
        )}
        <InputInnerWrapper
          {...{
            className: 'inputInnerWrap',
            background: props.background || InputBackground.Gray,
            error: !!props.error,
            warning: !!props.warning,
            disabled: props.disabled,
            focus: props.focus,
            blue: props.blue,
          }}
        >
          <StyledInput {...inputOptions} />
          {props.isAnimated && <label htmlFor={props.name} title={titleText} data-title={titleText}></label>}
          {!!props.button && <div {...{ className: 'button-wrapper', ref: buttonRef }}>{props.button}</div>}
          {showCurrencySymbol && <CurrencySymbol isAnimated={props.isAnimated}>$</CurrencySymbol>}
        </InputInnerWrapper>
      </StyledInputWrapper>
      {errorBox || warningBox}
    </fieldset>
  );
};

Input.defaultProps = {
  type: InputType.Text,
  disabled: false,
  enableAutoComplete: false,
  blue: false,
  value: '',
  centeredText: false,
  isSecuredInput: false,
};

const styledInput = styled(Input)`
  ${styles.InputOuterWrap}
`;

export { styledInput as Input };
